import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Modal } from "antd";
import { ErrorTypes } from "constants/ml.constants";
import { setError, setSummary } from "store/features/ml/mlSlice";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useUserUnit } from "../../hooks";
import { ChartDashboard } from "./ChartDashboard";
import FeatureSelector from "./FeatureSelector";
import InvalidDataDetailTable from "./InvalidDataDetailTable";
import SingularValueDetailTable from "./SingularValueDetailTable";
import { TargetSelector } from "./TargetSelector";
import { FeatureImportanceSummary, MlFeature } from "./types";
import { getFeatureDisplayNameWithProductAndUnits } from "./utils/nameUtils";

const RootContainer = styled.div<{ isFullscreen?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  padding: ${(props) => (props.isFullscreen ? "0px" : "10px")};
  gap: 10px;

  h5 {
    font-size: 1.7rem;
  }
`;

const FeatureImportanceSidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 40%;
  max-width: 300px;
  min-width: 175px;

  .ant-card-body {
    padding: 10px;
  }
`;

const ChartWrapper = styled.div<{ $isFullscreen?: boolean }>`
  width: 100%;
  height: 100%;

  ${(props) =>
    props.$isFullscreen
      ? `
    background: white;
    display: flex;
    flex-direction: column;
    overflow: auto;
  `
      : `
    display: grid;
    grid-template-columns: minmax(0, 1fr);
  `}
`;

const FeatureImportance = ({ onFullscreenToggle }) => {
  const dispatch = useDispatch();
  const unitType = useUserUnit();
  const mlState = useSelector((state: RootState) => state.ml);

  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const featureImportanceSummary =
    mlState.featureImportanceSummary as FeatureImportanceSummary;
  const wellTrackingData = featureImportanceSummary?.["well_tracking_data"] || {};

  const handleChartFullscreen = useCallback(
    (chartKey: string | null) => {
      setFullscreenChart(chartKey);

      // Trigger widget fullscreen toggle when entering or exiting chart fullscreen
      if (onFullscreenToggle) {
        onFullscreenToggle(chartKey !== null);
      }
    },
    [onFullscreenToggle]
  );

  const handleDataChange = useCallback(
    (data: FeatureImportanceSummary) => {
      dispatch(setSummary(data));
    },
    [dispatch]
  );

  useEffect(() => {
    if (wellTrackingData && wellTrackingData.droppedWellCount > 0) {
      dispatch(
        setError({
          code: ErrorTypes.InvalidData,
          details: wellTrackingData
        })
      );
    }
  }, [wellTrackingData]);

  const getFeatureDisplayName = (item: MlFeature) => {
    if (!item) return "";
    return getFeatureDisplayNameWithProductAndUnits(item, unitType);
  };

  const renderInvalidDataTableModal = useMemo(() => {
    return (
      <Modal
        open={errorModalVisible && mlState.errorType === ErrorTypes.InvalidData}
        onCancel={() => setErrorModalVisible(false)}
        title="Features Summary per Well"
        width={800}
        footer={null}
        bodyStyle={{
          maxHeight: "70vh",
          padding: "16px",
          display: "flex",
          flexDirection: "column"
        }}>
        <InvalidDataDetailTable getFeatureDisplayName={getFeatureDisplayName} />
      </Modal>
    );
  }, [errorModalVisible, mlState.warnDetails, mlState.inputFeatures]);

  const renderSingularValueTableModal = useMemo(() => {
    return (
      <Modal
        open={errorModalVisible && mlState.errorType === ErrorTypes.SingularValue}
        onCancel={() => setErrorModalVisible(false)}
        title="Features with Singular Values"
        width={800}
        footer={null}
        bodyStyle={{
          maxHeight: "70vh",
          padding: "16px",
          display: "flex",
          flexDirection: "column"
        }}>
        <SingularValueDetailTable getFeatureDisplayName={getFeatureDisplayName} />
      </Modal>
    );
  }, [errorModalVisible, mlState.warnDetails, mlState.inputFeatures]);

  return (
    <RootContainer
      id="feature-importance"
      className="feature-importance-app"
      data-testid="widget-feature-importance"
      isFullscreen={!!fullscreenChart}>
      {renderInvalidDataTableModal}
      {renderSingularValueTableModal}
      {!fullscreenChart && (
        <FeatureImportanceSidePanel>
          <TargetSelector setErrorModalVisible={setErrorModalVisible} />
          <FeatureSelector setErrorModalVisible={setErrorModalVisible} />
        </FeatureImportanceSidePanel>
      )}
      <ChartWrapper $isFullscreen={!!fullscreenChart}>
        <ChartDashboard
          fullscreenChart={fullscreenChart}
          onChartFullscreen={handleChartFullscreen}
          data={featureImportanceSummary}
          onDataChange={handleDataChange}
        />
      </ChartWrapper>
    </RootContainer>
  );
};

export default memo(FeatureImportance);
